/**
 * Fetch posts from a Bluesky account using the public API.
 * No authentication required — only public posts are returned.
 */

import type { ImportedPost } from './importParsers';

const PUBLIC_API = 'https://public.api.bsky.app';
const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

interface BskyFeedItem {
  post: {
    uri: string;
    cid: string;
    author: {
      handle: string;
      displayName?: string;
    };
    record: {
      text: string;
      createdAt: string;
      reply?: unknown;
    };
  };
  reply?: unknown;
}

interface BskyFeedResponse {
  feed: BskyFeedItem[];
  cursor?: string;
}

/**
 * Fetch up to `limit` original posts from a Bluesky handle.
 * Replies are skipped. Uses cursor-based pagination.
 */
export async function fetchBlueskyPosts(
  handle: string,
  limit = 200,
  signal?: AbortSignal,
): Promise<ImportedPost[]> {
  const cleanHandle = handle.trim().replace(/^@/, '');
  if (!cleanHandle) throw new Error('Please enter a Bluesky handle');

  const allPosts: ImportedPost[] = [];
  let cursor: string | undefined;
  const batchSize = 50; // max per API call

  while (allPosts.length < limit) {
    const params = new URLSearchParams({
      actor: cleanHandle,
      limit: String(batchSize),
      filter: 'posts_no_replies',
    });
    if (cursor) params.set('cursor', cursor);

    const url = `${PUBLIC_API}/xrpc/app.bsky.feed.getAuthorFeed?${params}`;
    const proxiedUrl = `${CORS_PROXY}${encodeURIComponent(url)}`;

    const res = await fetch(proxiedUrl, { signal });
    if (!res.ok) {
      if (res.status === 400) throw new Error(`Account "${cleanHandle}" not found on Bluesky`);
      throw new Error(`Bluesky API error: ${res.status}`);
    }

    const data = (await res.json()) as BskyFeedResponse;
    if (!data.feed || data.feed.length === 0) break;

    for (const item of data.feed) {
      // Skip replies (extra safety — filter should handle this)
      if (item.reply || item.post.record.reply) continue;

      const text = item.post.record.text?.trim();
      if (!text) continue;

      const createdAt = Math.floor(new Date(item.post.record.createdAt).getTime() / 1000);
      const uri = item.post.uri;
      const parts = uri.split('/');
      const rkey = parts.pop();
      const did = parts[2];

      allPosts.push({
        id: `bluesky-${allPosts.length}`,
        content: text,
        createdAt,
        source: 'Bluesky',
        url: `https://bsky.app/profile/${did}/post/${rkey}`,
        selected: true,
      });

      if (allPosts.length >= limit) break;
    }

    cursor = data.cursor;
    if (!cursor) break;
  }

  return allPosts;
}
